import { type FC, type MouseEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useResetRecoilState, useSetRecoilState } from 'recoil'

import { itemAtomFamily, unsavedChangesAtom } from '@/atoms/_editorAtoms'
import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import {
  DeleteIcon,
  LockIcon,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import { ClassicResizeHandle } from '@/components/page/Items/ItemControlOverlay/classic/ClassicItemControlOverlay.styled'
import { createSyntheticMouseEvent } from '@/components/page/Items/ItemControlOverlay/controls/CommonControls'

/**
 * The toolbar and resize controls of a classic (TinyMCE-era) item, identical
 * to staging's; the editor items have their own in
 * `controls/CommonControls.tsx`. The drag handle is the same for both and
 * lives there too.
 */

/**
 * Lock/Unlock icon - always visible, leftmost in toolbar
 */
export const ClassicCommonControls: FC<{ itemsLocked: boolean }> = ({
  itemsLocked,
}) => {
  const { t } = useTranslation()

  return (
    <Tooltip
      text={itemsLocked ? t('itemControls.locked') : t('itemControls.unlocked')}
    >
      <LockIcon
        icon={itemsLocked ? 'lockIcon' : 'unlockedIcon'}
        aria-label={
          itemsLocked ? t('itemControls.locked') : t('itemControls.unlocked')
        }
        data-locked={itemsLocked ? 'true' : 'false'}
        data-testid="overlay-lock-icon"
      />
    </Tooltip>
  )
}

/**
 * Delete button - rightmost in toolbar
 * Always visible regardless of lock state - users can remove items even when locked
 */
export const ClassicDeleteControls: FC<{
  itemId: string
  itemsLocked: boolean
}> = ({ itemId }) => {
  const { t } = useTranslation()
  const deleteItem = useResetRecoilState(itemAtomFamily(itemId))
  const resetControlState = useResetRecoilState(
    itemControlStateAtomFamily(itemId),
  )
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)

  const handleDelete = useCallback(() => {
    resetControlState()
    deleteItem()
    setUnsavedChanges(true)
  }, [deleteItem, resetControlState, setUnsavedChanges])

  return (
    <Tooltip text={t('itemControls.delete')}>
      <DeleteIcon
        onClick={handleDelete}
        icon="trashIcon"
        aria-label={t('itemControls.delete')}
        data-testid="overlay-delete-icon"
      />
    </Tooltip>
  )
}

/**
 * Resize handle - positioned at bottom-right corner of item
 */
export const ClassicResizeControls: FC<{
  itemId: string
  itemsLocked: boolean
  isLineItem: boolean
  zoom: number
}> = ({ itemId, itemsLocked, isLineItem, zoom }) => {
  const { t } = useTranslation()

  const handleResizeMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const resizeHandle = document.querySelector(
        `[data-resize-handle="${itemId}"]`,
      )
      if (!resizeHandle) return
      resizeHandle.dispatchEvent(createSyntheticMouseEvent(e))
    },
    [itemId],
  )

  if (isLineItem || itemsLocked) return null

  // Scale the entire resize control (including tooltip) to match toolbar scaling
  const scale = Math.min(2, Math.max(0.5, 100 / zoom))

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '0',
        right: '0',
        pointerEvents: 'auto',
        transform: `scale(${scale})`,
        transformOrigin: 'bottom right',
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <Tooltip
        text={t('itemControls.resize')}
        placement="right"
        offsetX={8}
        padding="10px 6px"
      >
        <ClassicResizeHandle
          $cursor="se"
          onMouseDown={handleResizeMouseDown}
          aria-label={t('itemControls.resize')}
          data-testid="overlay-resize-wedge"
        />
      </Tooltip>
    </div>
  )
}
